import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Typography,
} from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import Background from "../../../components/Background";
import Topnav from "../../../components/Topnav";
import ArrowBackIcon from "../../../assets/images/Arrow Left.svg";
import { baseFetch } from "../../../helpers/apiClient";

const PolicyPage = ({ type }) => {
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(false);
    setBody("");

    baseFetch({
      url: "/v2/auth/policies/current",
      method: "GET",
    })
      .then((response) => {
        if (cancelled) return;

        const policyBody =
          response?.data?.policies?.find((policy) => policy.type === type)
            ?.body ?? "";
        setBody(policyBody);
        setError(!policyBody.trim());
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [type]);

  const handleBack = () => {
    window.close();
    setTimeout(() => navigate("/onboarding", { replace: true }), 150);
  };

  return (
    <Background>
      <Topnav />
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
        <IconButton
          onClick={handleBack}
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "#2A4876",
            color: "white",
            borderRadius: "20px",
            padding: "8px 16px",
            "&:hover": { bgcolor: "#1F3553" },
            mb: 2,
          }}
          aria-label="Back"
        >
          <Box
            sx={{ display: "flex", alignItems: "center", marginRight: "8px" }}
          >
            <img src={ArrowBackIcon} alt="" />
          </Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>
            Back
          </Typography>
        </IconButton>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress aria-label="Loading policy" />
          </Box>
        )}
        {!loading && error && (
          <Alert severity="error">Unable to load policy.</Alert>
        )}
        {!loading && !error && (
          <Box
            sx={{
              padding: { xs: 2, sm: 4 },
              backgroundColor: "white",
              borderRadius: 2,
              overflowWrap: "anywhere",
              "& p": { mb: 2, lineHeight: 1.65 },
              "& h1, & h2, & h3": { mt: 3, mb: 1, lineHeight: 1.3 },
              "& ul, & ol": { pl: 3, mb: 2 },
              "& li": { mb: 0.5, lineHeight: 1.6 },
              "& a": { color: "#2A4876", overflowWrap: "anywhere" },
              "& table": {
                width: "100%",
                borderCollapse: "collapse",
                mb: 2,
              },
              "& th, & td": {
                border: "1px solid #AAB6C8",
                p: 1,
                textAlign: "left",
              },
            }}
          >
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Keep fetched policy Markdown from turning external links into
                // a way to navigate the current policy page away.
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" />
                ),
              }}
            >
              {body}
            </Markdown>
          </Box>
        )}
      </Container>
    </Background>
  );
};

export default PolicyPage;
